import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Grid2 as Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CardIcon,
} from '@mui/icons-material';
import { settingsService, CardType } from '../../../services';

const CardTypes: React.FC = () => {
  const [cardTypes, setCardTypes] = useState<CardType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [selected, setSelected] = useState<CardType | null>(null);
  const [formData, setFormData] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    icone: '',
    cor: '#000000',
    ordem: 0,
  });

  const fetchCardTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await settingsService.getCardTypes();
      setCardTypes(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar tipos de cartao');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCardTypes();
  }, [fetchCardTypes]);

  const handleOpenEdit = (cardType?: CardType) => {
    if (cardType) {
      setSelected(cardType);
      setFormData({
        codigo: cardType.codigo,
        nome: cardType.nome,
        descricao: cardType.descricao || '',
        icone: cardType.icone || '',
        cor: cardType.cor || '#000000',
        ordem: cardType.ordem || 0,
      });
    } else {
      setSelected(null);
      setFormData({ codigo: '', nome: '', descricao: '', icone: '', cor: '#000000', ordem: 0 });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (selected) {
        await settingsService.updateCardType(selected.id, formData);
      } else {
        await settingsService.createCardType(formData);
      }
      setEditDialog(false);
      fetchCardTypes();
    } catch (err: any) {
      setError(err.message || 'Erro ao guardar');
    }
  };

  const handleDelete = async (cardType: CardType) => {
    if (!window.confirm(`Desativar "${cardType.nome}"?`)) return;
    try {
      await settingsService.deleteCardType(cardType.id);
      fetchCardTypes();
    } catch (err: any) {
      setError(err.message || 'Erro ao desativar');
    }
  };

  if (loading && cardTypes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Tipos de Cartao
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          Novo Tipo
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={60}>Cor</TableCell>
                <TableCell>Codigo</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Descricao</TableCell>
                <TableCell>Ordem</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center" width={120}>Accoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cardTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CardIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">Nenhum tipo de cartao</Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpenEdit()} sx={{ mt: 2 }}>
                      Adicionar
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                cardTypes.map((ct) => (
                  <TableRow key={ct.id} hover>
                    <TableCell>
                      <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: ct.cor || '#ccc' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                        {ct.codigo}
                      </Typography>
                    </TableCell>
                    <TableCell>{ct.nome}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{ct.descricao}</Typography>
                    </TableCell>
                    <TableCell>{ct.ordem}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ct.estado === 'A' ? 'Activo' : 'Inactivo'}
                        color={ct.estado === 'A' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenEdit(ct)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(ct)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selected ? 'Editar Tipo de Cartao' : 'Novo Tipo de Cartao'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Codigo"
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                placeholder="Ex: VISA"
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Visa"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Cor"
                value={formData.cor}
                onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                type="color"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Ordem"
                value={formData.ordem}
                onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                type="number"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!formData.codigo || !formData.nome}>
            {selected ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CardTypes;
